import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Form } from 'tea-component';
import ProjectInfo from '@sg/components/ProjectInfo';
import ContentCard from '@sg/components/ContentCard';
import COSFileList from '@sg/components/COSFileList';
import type { ContentCardFile } from '@sg/components/ContentCard/types';
import { renderGateReviewTag } from '@sg/pages/StudioManagement/InDevProjects/pageConfig';
import GeneralModal from '@gp/components/GeneralModal';
import './index.scss';

interface IGRDecisionData {
  gate_review?: number;
  gate_review_decision?: string;
  key_feedback?: string;
  gr_attachments?: string | ContentCardFile[];
  kf_attachments?: string | ContentCardFile[];
}

interface IProps {
  grDecision?: IGRDecisionData;
  projectName?: string;
  projectCover?: string;
  milestoneName?: string;
}

const CLS = 'dbs-idp-gr-decision-cell';
const MAX_PREVIEW_CONTENT_ROWS_WITH_GATE_REVIEW = 2;
const MAX_PREVIEW_CONTENT_ROWS_WITHOUT_GATE_REVIEW = 3;

const stripHtml = (content: string): string => {
  if (!content) {
    return '';
  }
  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
};

const parseAttachments = (
  attachments: string | ContentCardFile[] | undefined,
): ContentCardFile[] => {
  if (Array.isArray(attachments)) {
    return attachments.map(item => ({
      ...item,
      name: item?.name || item?.path?.split('/').pop() || '',
    }));
  }
  if (!attachments || typeof attachments !== 'string' || !attachments.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(attachments);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((item: any) => ({
      ...item,
      name: item?.name || item?.file || item?.path?.split('/').pop() || '',
    }));
  } catch {
    return [];
  }
};

const GRDecisionCell: React.FC<IProps> = ({
  grDecision,
  projectName,
  projectCover,
  milestoneName,
}) => {
  const [visible, setVisible] = useState(false);

  const decisionText = useMemo(
    () => stripHtml(grDecision?.gate_review_decision || ''),
    [grDecision?.gate_review_decision],
  );
  const keyFeedbackText = useMemo(
    () => stripHtml(grDecision?.key_feedback || ''),
    [grDecision?.key_feedback],
  );
  const gateReviewValue = grDecision?.gate_review;
  const hasGateReview = [1, 2, 3, 4].includes(gateReviewValue || 0);
  const hasDecisionText = Boolean(decisionText);

  const grAttachments = useMemo(
    () => parseAttachments(grDecision?.gr_attachments),
    [grDecision?.gr_attachments],
  );
  const kfAttachments = useMemo(
    () => parseAttachments(grDecision?.kf_attachments),
    [grDecision?.kf_attachments],
  );
  const previewTextRef = useRef<HTMLDivElement | null>(null);
  const [showMoreBtn, setShowMoreBtn] = useState(false);
  const [previewTextLayout, setPreviewTextLayout] = useState<{
    isOverflow: boolean;
    usedRows: number;
  }>({
    isOverflow: false,
    usedRows: 0,
  });

  const hasDecisionSectionData = hasDecisionText || grAttachments.length > 0;
  const hasFeedbackSectionData = Boolean(keyFeedbackText) || kfAttachments.length > 0;
  // Preview prioritizes GR Decision section as a whole:
  // if either decision text or decision attachments exists, show GR Decision pair;
  // otherwise fallback to Key Feedback pair.
  const previewUsesDecision = hasDecisionSectionData;
  const previewText = previewUsesDecision ? decisionText : keyFeedbackText;
  const previewAttachments = previewUsesDecision ? grAttachments : kfAttachments;
  const hasPreviewText = Boolean(previewText);
  const hasPreviewAttachments = previewAttachments.length > 0;
  // Gate review tag occupies vertical space, so preview row cap is lower when it exists.
  const maxPreviewContentRows = hasGateReview
    ? MAX_PREVIEW_CONTENT_ROWS_WITH_GATE_REVIEW
    : MAX_PREVIEW_CONTENT_ROWS_WITHOUT_GATE_REVIEW;
  const previewTextLineLimit = hasPreviewText ? maxPreviewContentRows : 0;
  // Text-first allocation: files can only use the remaining row budget.
  const remainingRowsForAttachments = hasPreviewText
    ? Math.max(0, maxPreviewContentRows - previewTextLayout.usedRows)
    : maxPreviewContentRows;
  const previewAttachmentLimit = remainingRowsForAttachments;
  const previewDisplayAttachments = previewAttachments.slice(0, previewAttachmentLimit);
  const hasHiddenPreviewAttachments = previewAttachments.length > previewDisplayAttachments.length;
  const hasPreviewContent = hasPreviewText || hasPreviewAttachments;
  const hasHiddenSecondaryContent = previewUsesDecision
    ? hasFeedbackSectionData
    : hasDecisionSectionData;

  useEffect(() => {
    const checkOverflow = () => {
      let isTextOverflow = false;
      let usedRows = 0;
      if (previewTextRef.current && hasPreviewText) {
        const textEl = previewTextRef.current;
        isTextOverflow = Boolean(
          textEl.scrollHeight > textEl.clientHeight + 1 ||
          textEl.scrollWidth > textEl.clientWidth + 1,
        );
        if (isTextOverflow) {
          usedRows = maxPreviewContentRows;
        } else {
          const lineHeight = Number.parseFloat(window.getComputedStyle(textEl).lineHeight || '0');
          const fallbackRowHeight = 20;
          const realRowHeight = Number.isFinite(lineHeight) && lineHeight > 0
            ? lineHeight
            : fallbackRowHeight;
          // Rounded row count from rendered text height to calculate remaining budget for files.
          usedRows = Math.max(
            1,
            Math.min(maxPreviewContentRows, Math.ceil(textEl.clientHeight / realRowHeight)),
          );
        }
      }
      setPreviewTextLayout(prev => {
        if (prev.isOverflow === isTextOverflow && prev.usedRows === usedRows) {
          return prev;
        }
        return {
          isOverflow: isTextOverflow,
          usedRows,
        };
      });
      setShowMoreBtn(
        isTextOverflow ||
        hasHiddenSecondaryContent ||
        hasHiddenPreviewAttachments,
      );
    };

    checkOverflow();
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(checkOverflow);
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }
    return undefined;
  }, [
    hasPreviewText,
    previewText,
    maxPreviewContentRows,
    previewTextLineLimit,
    previewDisplayAttachments,
    hasHiddenSecondaryContent,
    hasHiddenPreviewAttachments,
  ]);

  const normalizedMilestoneName = useMemo(() => {
    if (!milestoneName) {
      return '';
    }
    return String(milestoneName).trim();
  }, [milestoneName]);

  const caption = normalizedMilestoneName
    ? `GR Decision and Feedback_${normalizedMilestoneName}`
    : 'GR Decision and Feedback';

  return (
    <div className={CLS}>
      <div className={`${CLS}__header`}>
        <span className={`${CLS}__title`}>GR Decision and Feedback</span>
      </div>

      <div className={`${CLS}__body`}>
        {hasGateReview && (
          <div className={`${CLS}__gate-review`}>
            {renderGateReviewTag(gateReviewValue)}
          </div>
        )}
        {previewText && (
          <div
            ref={previewTextRef}
            className={`${CLS}__decision-text`}
            style={{
              WebkitLineClamp: previewTextLineLimit,
              lineClamp: previewTextLineLimit,
            } as React.CSSProperties}
          >
            {previewText}
          </div>
        )}
        {previewDisplayAttachments.length > 0 && (
          <COSFileList
            className={classnames(
              `${CLS}__preview-file-list`,
              previewText && `${CLS}__preview-file-list--with-text`,
            )}
            domain="mgmt/studio/in_dev/gr_management"
            cosConfigDomain="gate_review_material,mgmt/studio/in_dev/gr_management"
            files={previewDisplayAttachments.map(file => {
              const splitFileName = (fileName?: string) => {
                if (!fileName || typeof fileName !== 'string') return { name: '', format: '' };
                const lastDotIndex = fileName.lastIndexOf('.');
                if (lastDotIndex <= 0) {
                  return { name: fileName, format: '' };
                }
                return {
                  name: fileName.substring(0, lastDotIndex),
                  format: fileName.substring(lastDotIndex), // includes dot
                };
              };

              const rawName = (typeof file?.name === 'string' && file.name)
                || (typeof file?.path === 'string' && file.path.split('/').pop())
                || '';
              const parsed = file?.format
                ? { name: rawName, format: file.format }
                : splitFileName(rawName);
              const fileNameWithoutExt = parsed.name || rawName;
              const fileExtension = parsed.format || '';
              const cleanFormat = (fileExtension || '').startsWith('.')
                ? fileExtension.substring(1)
                : (fileExtension || '');
              return {
                ...file,
                name: fileNameWithoutExt,
                format: cleanFormat,
                size: typeof file?.size === 'string' ? parseInt(file.size, 10) : file?.size || 0,
              };
            })}
          />
        )}
        {!hasGateReview && !hasPreviewContent && (
          <span className={`${CLS}__empty`}>-</span>
        )}
        {showMoreBtn && (
          <button
            type="button"
            className={`${CLS}__more-btn`}
            onClick={() => setVisible(true)}
          >
            More
          </button>
        )}
      </div>

      <GeneralModal
        visible={visible}
        onClose={() => setVisible(false)}
        title={caption}
        modalSize="xl"
        className="dbs-idp-gr-decision-modal"
        footerButtonGroup={['close']}
        closeButtonText="Close"
        content={
          <>
            <div className="dbs-idp-gr-decision-modal__project">
              <ProjectInfo name={projectName || '-'} src={projectCover} width={36} />
            </div>
            <Form>
              <Form.Item label="Gate Review Decision">
                {hasGateReview && renderGateReviewTag(gateReviewValue)}
                <ContentCard
                  domain="mgmt/studio/in_dev/gr_management"
                  items={grDecision?.gate_review_decision || 'No data'}
                  files={grAttachments}
                  renderHtml={true}
                  fileStyling={true}
                  className="dbs-gr-management-content"
                  cosConfigDomain="gate_review_material,mgmt/studio/in_dev/gr_management"
                />
              </Form.Item>
              <Form.Item label="Key Feedback">
                <ContentCard
                  domain="mgmt/studio/in_dev/gr_management"
                  items={grDecision?.key_feedback || 'No data'}
                  files={kfAttachments}
                  renderHtml={true}
                  fileStyling={true}
                  className="dbs-gr-management-content"
                  cosConfigDomain="gate_review_material,mgmt/studio/in_dev/gr_management"
                />
              </Form.Item>
            </Form>
          </>
        }
      />
    </div>
  );
};

export default GRDecisionCell;
